<template>
  <div :class="$style.blockPayment">
    <div v-for="payment in paymentsData" :key="payment.id" :class="$style.blockPaymentItem">
      <PaymentCard
        :payment="payment"
        :card-data="paymentCardData"
        :is-currency-match="isCurrencyMatch(payment)"
        @cardClick="() => openDeposit(payment.paymentMethodLogoId, payment.shouldOpenByCryptoModal)"
      />
    </div>
  </div>
</template>
<script lang="ts" setup>
import { PaymentCard } from '~/components/lib/payments';
import { useStoreModals } from '~/stores/abstract/modals';
import { useStorePages } from '~/stores/abstract/pages';
import { useStoreRoot } from '~/stores/abstract/root';
import { useStoreMenuStatuses } from '~/stores/client/menuStatuses';
import { useStoreSsPlayer } from '~/stores/softswiss/player';
import { useStoreCurrencyMenu } from '~/stores/strapi/currencyMenu';
import { EModalName } from '~/types/modals';
import { EPaymentsType } from '~/types/payments';
import { SettingsId } from '~/types/settings';
import type { ComponentInfoPageBlocksPaymentsBlock } from '~/types/infoPages';
import type { IPaymentData } from '~/types/payments';

const props = defineProps({
  data: {
    type: Object as PropType<ComponentInfoPageBlocksPaymentsBlock>,
    required: true,
  },
  payments: {
    type: Array as PropType<IPaymentData[]>,
    required: true,
  },
  deposit: {
    type: Boolean,
    required: true,
  },
  gtmLabel: {
    type: String,
    default: () => '',
  },
  gtmCategory: {
    type: String,
    default: () => '',
  },
  gtmAction: {
    type: String,
    default: () => '',
  },
});

const storeModals = useStoreModals();
const storeMenuStatuses = useStoreMenuStatuses();
const storeRoot = useStoreRoot();
const storeSsPlayer = useStoreSsPlayer();
const storeCurrencyMenu = useStoreCurrencyMenu();

/**
 * Function for getting data for Info Pages blocks and
 * getting data for Payments Data from strapiand glue them
 */
const paymentsData = computed(() => {
  const data = props.data as { [key: string]: any };

  return props.payments.map((p) => ({
    ...p,
    paymentType:
      data['paymentType' + p.paymentType.slice(0, 1).toUpperCase() + p.paymentType.slice(1)] ||
      p.paymentType,
    paymentProcessingTime:
      data[
        'paymentProcessingTime' +
          p.paymentProcessingTime?.slice(0, 1).toUpperCase() +
          p.paymentProcessingTime?.slice(1)
      ] || p.paymentProcessingTime,
  }));
});

const paymentCardData = computed(() => {
  return {
    tableTypeTitle: props.data.tableTypeTitle,
    tableFeeTitle: props.data.tableFeeTitle,
    tableProcessingTimeTitle: props.data.tableProcessingTimeTitle,
    tableLimitsTitle: props.data.tableLimitsTitle,
    paymentLimitsMin: props.data.paymentLimitsMin,
    paymentLimitsMax: props.data.paymentLimitsMax,
  };
});

const isCurrencyMatch = (payment: IPaymentData) => {
  if (props.deposit) {
    return false;
  }
  if (payment.type !== EPaymentsType.withdrawal) {
    return false;
  }
  if (!storeCurrencyMenu.isCryptoCurency(storeRoot.userCurrency)) {
    return false;
  }
  const userCurrency = (storeRoot.userCurrency || '').toLowerCase();
  const methodId = (payment.paymentMethodLogoId || '').toLowerCase();
  if (!userCurrency || !methodId) {
    return false;
  }
  return (
    methodId === userCurrency ||
    methodId.endsWith(`_${userCurrency}`) ||
    methodId.includes(`_${userCurrency}_`) ||
    methodId.startsWith(`${userCurrency}_`)
  );
};

const openDeposit = (paymentMethodLogoId: any, shouldOpenByCryptoModal?: boolean) => {
  if (!storeSsPlayer.isPlayerAuthorised) {
    storeModals.openLoginOrSignupModal({ button_place: 'payment_block_table' });
  } else {
    // if (storeModals.activeModal) {
    //   storeModals.closeModal(storeModals.activeModal.name as EModalName);
    // }
    if (storeSsPlayer.isPlayerCryptoCountry) {
      storeModals.openModal(EModalName.deposit);
    } else if (
      shouldOpenByCryptoModal === true &&
      storeCurrencyMenu.isCryptoCurency(storeRoot.userCurrency)
    ) {
      storeModals.openModal(EModalName.buy_crypto);
    } else if (storeRoot.isEstonianLicense && !storeSsPlayer.isDepositReady) {
      useStorePages().openAccountSettingsPage(SettingsId.Profile);
    } else {
      const modalName = props.deposit ? EModalName.deposit : EModalName.withdraw;
      storeModals.openModal(modalName, {
        ...(paymentMethodLogoId ? { paymentMethodLogoId } : {}),
        ...(props.deposit ? { gtmParams: { button_place: 'payment' } } : {}),
      });
    }

    storeMenuStatuses.toggleNotificationsMenu(false);
    storeMenuStatuses.toggleSidebarMenu(false);
  }
};
</script>

<style lang="scss" module src="./paymentBlockTable.scss"></style>
